import {auth} from "@/firebase/firebaseConfig";
import {
    ensureUserProfile,
    EnsureUserProfileOptions,
    EnsureUserProfileResult,
} from "@/lib/UserProfileEnsure";
import {getUserProfile, UserProfile} from "@/lib/UserProfile";
import {snapshotFromEnsure, translatorProfileFromSnapshot} from "@/lib/sessionSnapshot";
import {resolveAuthTokenForceRefresh} from "@/lib/authTokenRefresh";

const PROFILE_CACHE_TTL_MS = 30_000;
const profileCacheByUid = new Map<string, {profile: UserProfile; fetchedAt: number}>();

export interface AuthBootstrapResult {
    ensure: EnsureUserProfileResult;
    profile: UserProfile | null;
}

export interface AuthBootstrapOptions extends EnsureUserProfileOptions {
    /** When false, skips the direct Firestore profile read after ensure. Default true. */
    loadProfile?: boolean;
    /**
     * When true, forces a fresh ID token before ensure/profile reads.
     * Default false: getIdToken(false) returns a valid token and refreshes only
     * if it is expired. Force refresh right after password sign-in fails on a
     * weak network (auth/network-request-failed).
     */
    refreshToken?: boolean;
}

async function refreshAuthTokenForUid(uid: string, refreshToken: boolean): Promise<void> {
    const currentUser = auth.currentUser;
    if (!currentUser || currentUser.uid !== uid) {
        return;
    }

    await currentUser.getIdToken(refreshToken);
}

/**
 * Single auth-bootstrap entry point: refresh token, ensure profile callable,
 * then optionally load the Firestore profile with a short-lived cache.
 */
export async function bootstrapAuthUser(options: AuthBootstrapOptions = {}): Promise<AuthBootstrapResult> {
    const uid = options.uid || auth.currentUser?.uid;
    if (!uid) {
        throw new Error("Cannot bootstrap auth without uid");
    }

    const refreshToken = resolveAuthTokenForceRefresh(options.refreshToken);
    await refreshAuthTokenForUid(uid, refreshToken);

    const ensure = await ensureUserProfile({...options, uid});

    if (options.loadProfile === false) {
        return {ensure, profile: null};
    }

    if (ensure.onboarding) {
        return {ensure, profile: translatorProfileFromSnapshot(uid, snapshotFromEnsure(ensure))};
    }

    const cached = profileCacheByUid.get(uid);
    if (cached && Date.now() - cached.fetchedAt < PROFILE_CACHE_TTL_MS) {
        return {ensure, profile: cached.profile};
    }

    const profile = await getUserProfile(uid);
    if (profile) {
        profileCacheByUid.set(uid, {profile, fetchedAt: Date.now()});
    }

    return {ensure, profile};
}

export function clearAuthBootstrapProfileCache(uid?: string): void {
    if (uid) {
        profileCacheByUid.delete(uid);
        return;
    }
    profileCacheByUid.clear();
}

export function peekCachedAuthProfile(uid: string): UserProfile | null {
    const cached = profileCacheByUid.get(uid);
    if (!cached || Date.now() - cached.fetchedAt >= PROFILE_CACHE_TTL_MS) {
        return null;
    }
    return cached.profile;
}
